import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { POLLING_INTERVAL_MS } from "../../constants/polling-constants";
import { queryKeys } from "../../lib/queryKeys";
import {
  acknowledgeOpcdHimAlert,
  getOpcdHimAlerts,
} from "../../services";
import type {
  OpcdHimAlertsResponse,
  OpcdHimChecklist,
} from "../../types/opcd-him-alerts";

const extractOpcdHimAlerts = (payload: unknown): OpcdHimAlertsResponse => {
  const empty: OpcdHimAlertsResponse = {
    success: false,
    providerNpi: "",
    count: 0,
    checklists: [],
  };
  if (!payload || typeof payload !== "object") return empty;

  const root = payload as Record<string, unknown>;
  if (Array.isArray(root.checklists)) {
    return {
      success: Boolean(root.success),
      providerNpi: String(root.providerNpi ?? ""),
      count: Number(root.count ?? root.checklists.length),
      checklists: root.checklists as OpcdHimChecklist[],
    };
  }
  if (root.data && typeof root.data === "object") {
    return extractOpcdHimAlerts(root.data);
  }
  return empty;
};

const fetchOpcdHimAlerts = async (
  providerNpi: string
): Promise<OpcdHimAlertsResponse> => {
  const response = await getOpcdHimAlerts(providerNpi);
  return extractOpcdHimAlerts(response.data);
};

export const useOpcdHimAlerts = (
  providerNpi: string,
  options?: { polling?: boolean; enabled?: boolean }
) =>
  useQuery({
    queryKey: queryKeys.opcdHimAlerts(providerNpi),
    queryFn: () => fetchOpcdHimAlerts(providerNpi),
    enabled: (options?.enabled ?? Boolean(providerNpi)) && Boolean(providerNpi),
    refetchInterval: options?.polling ? POLLING_INTERVAL_MS : false,
  });

export const useOpcdHimAlertsCount = (
  providerNpi?: string,
  options?: { polling?: boolean; enabled?: boolean }
) =>
  useQuery({
    queryKey: queryKeys.opcdHimAlertsCount(providerNpi ?? ""),
    queryFn: async () => {
      const response = await fetchOpcdHimAlerts(providerNpi!);
      return response.checklists.reduce(
        (count, checklist) =>
          count +
          (checklist.errors ?? []).filter(
            (alert) => !alert.provider_acknowledged
          ).length,
        0
      );
    },
    enabled:
      (options?.enabled ?? Boolean(providerNpi)) && Boolean(providerNpi),
    refetchInterval: options?.polling ? POLLING_INTERVAL_MS : false,
  });

export const useAcknowledgeOpcdHimAlert = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      alertId,
      providerNpi,
    }: {
      alertId: string;
      providerNpi: string;
    }) => acknowledgeOpcdHimAlert(alertId, providerNpi),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["opcdHimAlerts"] });
      void queryClient.invalidateQueries({ queryKey: ["opcdHimAlertsCount"] });
    },
  });
};
